// MIDAS V3 — Thinking panel (MODEL-PICKER-11, spec §14.2)
//
// Collapsible panel for the bot's `event: thinking` summarized-reasoning
// deltas (thinking picks only: Sonnet 5 thinking / Opus 4.8 thinking —
// isThinkingModel set). Single-stream: no variant tag, one panel per turn.
//
// Two mount surfaces, same component:
//   • Streaming (ChatMessageListV3): fed from the per-thread buffer's
//     thinkingText. AUTO-EXPANDED while ONLY thinking streams
//     (autoExpanded=true); the parent flips autoExpanded to false the moment
//     answer tokens start → the panel default-collapses (user can re-expand).
//   • Completed turn (ChatBotMessageV3): fed from message.thinkingText,
//     autoExpanded=false → collapsed by default, re-expandable while the
//     turn is on screen.
//
// Interaction mirrors the retired compare spec's row-C panel (behavior
// reference only — this is single-stream). Rendered ABOVE the answer prose.
// `.cc`-gated by the CALLERS (isCcDevSurface) together with the picker.
//
// Reduced-motion: the pulsing dot uses the shared `midas-pulse` keyframe,
// gated by the global prefers-reduced-motion override in
// midas-v3-tokens.scss (animation-duration: 0.001ms). No height animation —
// expand/collapse is an instant swap (nothing to gate).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useEffect, useRef, useState } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";

export interface ThinkingPanelV3Props {
  /** The accumulated thinking transcript (may still be growing). */
  text: string;
  /** True while thinking deltas are still streaming in (drives the pulsing
   *  dot + auto-scroll of the transcript body). */
  streaming?: boolean;
  /** Parent-driven default expansion: true while ONLY thinking streams,
   *  false once answer tokens start / on the completed turn. A user toggle
   *  overrides it until autoExpanded next CHANGES (then the auto behavior
   *  takes over again — this is what produces "default-collapses once
   *  answer tokens start" without stealing a deliberate user expand later). */
  autoExpanded?: boolean;
}

const ChevronV3: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 10 6"
    width="9"
    height="6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{
      display: "block",
      transform: open ? "rotate(180deg)" : "none",
      transition: "transform 0.12s ease",
    }}
  >
    <path d="M1 1.2l4 3.6 4-3.6" />
  </svg>
);

const ThinkingPanelV3: React.FC<ThinkingPanelV3Props> = ({
  text,
  streaming = false,
  autoExpanded = false,
}) => {
  const { translate } = useTranslation("chatBot");

  // null = follow autoExpanded; boolean = user override. Reset the override
  // whenever autoExpanded changes so the auto-collapse on first answer token
  // always lands (§14.2 interaction).
  const [userToggled, setUserToggled] = useState<boolean | null>(null);
  useEffect(() => {
    setUserToggled(null);
  }, [autoExpanded]);
  const expanded = userToggled ?? autoExpanded;

  // Keep the transcript pinned to its bottom while deltas stream in.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (streaming && expanded && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [text, streaming, expanded]);

  const label = translate("midas.v3.thinking.label", {
    defaultValue: "Thinking",
  }) as string;
  const toggleAria = translate("midas.v3.thinking.toggleAria", {
    defaultValue: "Toggle reasoning",
  }) as string;

  if (!text) return null;

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        background: "var(--bg-soft)",
        overflow: "hidden",
        marginBottom: 8,
      }}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-label={toggleAria}
        onClick={() => setUserToggled(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          padding: "7px 12px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
        }}
      >
        {streaming && (
          <span
            aria-hidden="true"
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: "var(--accent)",
              flexShrink: 0,
              animation: "midas-pulse 1.2s ease-in-out infinite",
            }}
          />
        )}
        <span
          className="mono"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 10.5,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 600,
            color: streaming ? "var(--accent)" : "var(--muted)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
        <span style={{ color: "var(--faint)", display: "inline-flex" }}>
          <ChevronV3 open={expanded} />
        </span>
      </button>
      {expanded && (
        <div
          ref={bodyRef}
          style={{
            padding: "2px 12px 10px",
            maxHeight: 260,
            overflowY: "auto",
            fontSize: 12.5,
            lineHeight: 1.55,
            color: "var(--muted)",
            fontFamily: "var(--font-sans)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
};

export default ThinkingPanelV3;
